if (!customElements.get("media-gallery")) {
  customElements.define(
    "media-gallery",
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia("(min-width: 750px)");

        // Initialize main gallery arrows (desktop)
        this.initMainGalleryArrows();

        // Initialize mobile slider buttons
        this.initMobileSliderButtons();

        // Initialize thumbnail gallery arrows
        this.initThumbnailGallery();

        // Watch for variant changes that show/hide images
        this.observeVariantChanges();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener(
          "slideChanged",
          debounce(this.onSlideChanged.bind(this), 500)
        );
        this.elements.thumbnails
          .querySelectorAll("[data-target]")
          .forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector("button")
              .addEventListener(
                "click",
                this.setActiveMedia.bind(
                  this,
                  mediaToSwitch.dataset.target,
                  false
                )
              );
          });
        if (
          this.dataset.desktopLayout.includes("thumbnail") &&
          this.mql.matches
        )
          this.removeListSemantic();
      }

      initMainGalleryArrows() {
        const prevArrow = this.querySelector('.main-gallery-arrow--prev');
        const nextArrow = this.querySelector('.main-gallery-arrow--next');
        if (prevArrow) prevArrow.addEventListener('click', () => this.navigateMainGallery('prev'));
        if (nextArrow) nextArrow.addEventListener('click', () => this.navigateMainGallery('next'));
        this.updateMainGalleryArrows();
      }

      updateMainGalleryArrows() {
        // Desktop only - don't run on mobile
        if (window.innerWidth < 750) return;
        const prevArrow = this.querySelector('.main-gallery-arrow--prev');
        const nextArrow = this.querySelector('.main-gallery-arrow--next');
        if (!this.elements.thumbnails) return;
        const visibleItems = Array.from(
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item[data-target]')
        ).filter(item => window.getComputedStyle(item).display !== 'none');
        const shouldHide = visibleItems.length <= 1;
        if (prevArrow) prevArrow.style.display = shouldHide ? 'none' : '';
        if (nextArrow) nextArrow.style.display = shouldHide ? 'none' : '';
      }

      observeVariantChanges() {
        if (!this.elements.thumbnails) return;
        const observer = new MutationObserver(debounce(() => {
          this.updateMainGalleryArrows();
        }, 100));
        this.elements.thumbnails.querySelectorAll('.thumbnail-list__item').forEach(item => {
          observer.observe(item, { attributes: true, attributeFilter: ['style'] });
        });
      }

      navigateMainGallery(direction) {
        if (!this.elements.thumbnails) return;
        const thumbnailItems = Array.from(
          this.elements.thumbnails.querySelectorAll('.thumbnail-list__item[data-target]')
        ).filter(item => window.getComputedStyle(item).display !== 'none');
        if (thumbnailItems.length === 0) return;

        let currentIndex = thumbnailItems.findIndex(
          item => item.querySelector('button')?.getAttribute('aria-current') === 'true'
        );
        if (currentIndex === -1) currentIndex = 0;

        // Wrap-around navigation
        let newIndex;
        if (direction === 'prev') {
          newIndex = currentIndex - 1 < 0 ? thumbnailItems.length - 1 : currentIndex - 1;
        } else {
          newIndex = currentIndex + 1 >= thumbnailItems.length ? 0 : currentIndex + 1;
        }

        const mediaId = thumbnailItems[newIndex].dataset.target;

        // Directly swap is-active class — avoids triggering the SliderComponent's
        // scroll handler (which setActiveMedia does via parentElement.scrollTo),
        // preventing all slides from appearing stacked after navigation.
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => {
          el.classList.remove('is-active');
        });
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (activeMedia) {
          activeMedia.classList.add('is-active');
          this.playActiveMedia(activeMedia);
          this.preventStickyHeader();
        }

        // Sync thumbnail highlight + auto-scroll thumbnail strip
        this.setActiveThumbnail(thumbnailItems[newIndex]);

        // Announce to live region for accessibility
        if (activeMedia) {
          this.announceLiveRegion(activeMedia, thumbnailItems[newIndex].dataset.mediaPosition);
        }
      }

      initMobileSliderButtons() {
        const sliderPrevBtn = this.querySelector('.slider-button--prev');
        const sliderNextBtn = this.querySelector('.slider-button--next');
        const slider = this.querySelector('.product__media-list');
        if (!slider) return;

        const self = this;

        const getSlideItems = () =>
          Array.from(slider.querySelectorAll('.product__media-item')).filter(
            item => {
              const style = item.getAttribute('style') || '';
              const computed = window.getComputedStyle(item).display;
              // Check both inline style and computed style
              if (style.includes('display: none') || style.includes('display:none')) return false;
              return computed !== 'none';
            }
          );

        const getCurrentIndex = () => {
          const items = getSlideItems();
          if (!items.length) return 0;
          const scrollPos = slider.scrollLeft;
          // Find the item closest to current scroll position
          let closestIdx = 0;
          let minDiff = Infinity;
          items.forEach((item, idx) => {
            const diff = Math.abs(item.offsetLeft - scrollPos);
            if (diff < minDiff) {
              minDiff = diff;
              closestIdx = idx;
            }
          });
          return closestIdx;
        };

        const updateButtons = () => {
          if (window.innerWidth >= 750) return; // Mobile only
          
          const items = getSlideItems();
          const idx = getCurrentIndex();
          const currentEl = self.querySelector('.slider-counter--current');
          const totalEl = self.querySelector('.slider-counter--total');
          
          if (currentEl) currentEl.textContent = idx + 1;
          if (totalEl) totalEl.textContent = items.length;
          
          // Hide both buttons if only 1 or no images
          if (items.length <= 1) {
            if (sliderPrevBtn) {
              sliderPrevBtn.style.visibility = 'hidden';
              sliderPrevBtn.setAttribute('disabled', 'disabled');
            }
            if (sliderNextBtn) {
              sliderNextBtn.style.visibility = 'hidden';
              sliderNextBtn.setAttribute('disabled', 'disabled');
            }
            return;
          }
          
          // Multiple images: enable buttons and set visibility based on position
          // Remove disabled attribute that SliderComponent might have set
          if (sliderPrevBtn) {
            sliderPrevBtn.removeAttribute('disabled');
            sliderPrevBtn.style.visibility = idx === 0 ? 'hidden' : 'visible';
          }
          if (sliderNextBtn) {
            sliderNextBtn.removeAttribute('disabled');
            sliderNextBtn.style.visibility = idx === items.length - 1 ? 'hidden' : 'visible';
          }
        };

        const goToSlide = (index) => {
          const items = getSlideItems();
          if (!items.length || index < 0 || index >= items.length) return;
          slider.scrollTo({ left: items[index].offsetLeft, behavior: 'smooth' });
          setTimeout(updateButtons, 300);
        };

        const resetToFirstSlide = () => {
          if (window.innerWidth >= 750) return; // Mobile only
          const items = getSlideItems();
          if (items.length > 0) {
            slider.scrollTo({ left: items[0].offsetLeft, behavior: 'auto' });
          }
          setTimeout(updateButtons, 50);
        };

        // Snap to nearest visible item after scroll ends
        const snapToVisibleItem = debounce(() => {
          const items = getSlideItems();
          if (items.length <= 1) return;
          const idx = getCurrentIndex();
          slider.scrollTo({ left: items[idx].offsetLeft, behavior: 'smooth' });
          updateButtons();
        }, 150);

        if (sliderPrevBtn) {
          sliderPrevBtn.addEventListener('click', (e) => {
            // Mobile only: prevent SliderComponent from also handling this click
            if (window.innerWidth < 750) {
              e.preventDefault();
              e.stopPropagation();
              goToSlide(getCurrentIndex() - 1);
            }
          });
        }
        if (sliderNextBtn) {
          sliderNextBtn.addEventListener('click', (e) => {
            // Mobile only: prevent SliderComponent from also handling this click
            if (window.innerWidth < 750) {
              e.preventDefault();
              e.stopPropagation();
              goToSlide(getCurrentIndex() + 1);
            }
          });
        }

        slider.addEventListener('scroll', debounce(updateButtons, 100));
        slider.addEventListener('scrollend', () => {
          if (window.innerWidth < 750) snapToVisibleItem();
        });

        // Watch for variant changes - observe style changes on media items
        const mediaItems = slider.querySelectorAll('.product__media-item');
        if (mediaItems.length > 0) {
          const observer = new MutationObserver(debounce(() => {
            if (window.innerWidth < 750) {
              // Get the parent slider-component and force it to reinitialize its pages
              // This ensures sliderItemsToShow and sliderItemOffset are recalculated
              // after images become visible/hidden due to variant change
              const sliderComponent = self.elements.viewer;
              if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
                sliderComponent.resetPages();
              }
              resetToFirstSlide();
              // Re-enable buttons after SliderComponent may have disabled them
              // Use a slight delay to ensure DOM updates are complete
              setTimeout(() => {
                const items = getSlideItems();
                if (items.length > 1) {
                  if (sliderPrevBtn) {
                    sliderPrevBtn.removeAttribute('disabled');
                  }
                  if (sliderNextBtn) {
                    sliderNextBtn.removeAttribute('disabled');
                  }
                }
                updateButtons();
              }, 100);
            }
          }, 200));
          mediaItems.forEach(item => {
            observer.observe(item, { attributes: true, attributeFilter: ['style', 'class'] });
          });
        }

        // Also listen for the var_sku_change event as a backup for variant changes
        // This ensures slider reinitializes even if MutationObserver misses some changes
        window.addEventListener('var_sku_change', debounce(() => {
          if (window.innerWidth < 750) {
            // Delay to allow DOM updates from variant change to complete
            setTimeout(() => {
              const sliderComponent = self.elements.viewer;
              if (sliderComponent && typeof sliderComponent.resetPages === 'function') {
                sliderComponent.resetPages();
              }
              resetToFirstSlide();
              setTimeout(updateButtons, 100);
            }, 250);
          }
        }, 100));

        setTimeout(updateButtons, 100);
      }

      initThumbnailGallery() {
        const upArrow = this.querySelector('.thumbnail-arrow--up');
        const downArrow = this.querySelector('.thumbnail-arrow--down');
        const thumbnailList = this.querySelector('.thumbnail-slider__list, .thumbnail-list');
        if (!thumbnailList) return;

        const scrollAmount = 100;

        const updateArrows = () => {
          // Hide both arrows if no scrolling needed
          const needsScroll = thumbnailList.scrollHeight > thumbnailList.clientHeight + 1;
          if (!needsScroll) {
            if (upArrow) upArrow.style.display = 'none';
            if (downArrow) downArrow.style.display = 'none';
            return;
          }
          if (upArrow) upArrow.style.display = '';
          if (downArrow) downArrow.style.display = '';

          const atTop = thumbnailList.scrollTop <= 1;
          const atBottom = thumbnailList.scrollTop + thumbnailList.clientHeight >= thumbnailList.scrollHeight - 1;
          if (upArrow) upArrow.disabled = atTop;
          if (downArrow) downArrow.disabled = atBottom;
        };

        if (upArrow) {
          upArrow.addEventListener('click', (e) => {
            e.preventDefault();
            thumbnailList.scrollBy({ top: -scrollAmount, behavior: 'smooth' });
            setTimeout(updateArrows, 300);
          });
        }

        if (downArrow) {
          downArrow.addEventListener('click', (e) => {
            e.preventDefault();
            thumbnailList.scrollBy({ top: scrollAmount, behavior: 'smooth' });
            setTimeout(updateArrows, 300);
          });
        }

        thumbnailList.addEventListener('scroll', debounce(updateArrows, 100));

        // Re-check whenever the list's scroll height changes (images loading in,
        // variant switches, window resize, etc.)
        if (typeof ResizeObserver !== 'undefined') {
          new ResizeObserver(debounce(updateArrows, 80)).observe(thumbnailList);
        }

        // Also fire when each thumbnail image finishes loading — layout
        // isn't stable until all <img> elements have their natural dimensions.
        thumbnailList.querySelectorAll('img').forEach((img) => {
          if (!img.complete) {
            img.addEventListener('load', updateArrows, { once: true });
            img.addEventListener('error', updateArrows, { once: true });
          }
        });

        // Staggered fallback checks to catch any remaining edge-cases
        // (e.g. lazy-loaded images that resolve after the ResizeObserver fires)
        setTimeout(updateArrows, 100);
        setTimeout(updateArrows, 500);
        setTimeout(updateArrows, 1200);
      }

      onSlideChanged(event) {
        var flagSkuConditionFlag = false;
        document.querySelectorAll(`.product__media-item.grid__item`).forEach((productForm) => {
          if (productForm.classList.contains("flagSkuCondition") && !flagSkuConditionFlag) {
            flagSkuConditionFlag = true;
          }
        });
        if( typeof event.detail.currentElement != "undefined" ){
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
          if (typeof colorArray !== 'undefined' && colorArray.length > 0 && true) {
            setTimeout(() => {
              var count = 0;
              document.querySelectorAll(`.product__media-item.grid__item`).forEach((productForm) => {
                if(productForm.style.display == 'block'){
                  count++;
                }              
              });
              document.querySelector('.slider-counter .slider-counter--total').innerHTML = count;
              
              /* hide arrow button */
              var curentCount = document.querySelector(".slider-counter .slider-counter--current").innerHTML;
              var totalCount = document.querySelector(".slider-counter .slider-counter--total").innerHTML;                      
              const buttonElementNext = document.querySelector(".slider-buttons .slider-button.slider-button--next");
              if (curentCount === totalCount) {              
                buttonElementNext.disabled = true;
              }else{
                buttonElementNext.disabled = false;
              }
              this.setActiveThumbnail(thumbnail);
            },200);
          }
        }else{
          if (typeof colorArray !== 'undefined' && colorArray.length > 0 ) {
            currNotHideMedia = [];
            var cntMed = 1;
            if( window.screen.width > 768 ) {    
              document.querySelectorAll(`.thumbnail-list__item`).forEach((productForm) => {
                if( productForm.style.display == "block" ) {
                  var mediaId = productForm.getAttribute('data-target');
                  currNotHideMedia.push(mediaId);
                }
              });
            } else{
              document.querySelectorAll(`.product__media-item.grid__item`).forEach((productForm) => {      
                if( productForm.style.display == "block" ) {
                  var mediaId = productForm.getAttribute('data-media-id');
                  currNotHideMedia.push(mediaId);
                }
              });
            }
            document.querySelectorAll(`.product__media-item.grid__item`).forEach((productForm) => {      
              if( productForm.style.display == "block" && cntMed == 1 ) {
                productForm.style.display = 'block';
                cntMed++;
              } else{
                productForm.style.display = 'none';
              }
            });
            
            document.querySelectorAll(`.thumbnail-list__item`).forEach((productForm) => {
              productForm.querySelector('button.thumbnail').addEventListener('click', function(){
                var thumbId = $(this).parent('li').attr('data-target');            
                $(".product__media-item.grid__item").each(function(){                
                  var mediaId = $(this).attr('data-media-id');
                  if( mediaId ==  thumbId ){
                    $(this).css('display', 'block');
                  } else{
                    $(this).css('display', 'none');
                  }
                });
              });
            });
          }
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia = this.elements.viewer.querySelector(
          `[data-media-id="${mediaId}"]`
        );
        this.elements.viewer
          .querySelectorAll("[data-media-id]")
          .forEach((element) => {
            element.classList.remove("is-active");
          });
        activeMedia.classList.add("is-active");

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
            });
          }
          if (
            !this.elements.thumbnails ||
            this.dataset.desktopLayout === "stacked"
          ) {
            activeMedia.scrollIntoView({ behavior: "smooth" });
          }
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(
          activeMedia,
          activeThumbnail.dataset.mediaPosition
        );
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll("button")
          .forEach((element) => element.removeAttribute("aria-current"));
        thumbnail.querySelector("button").setAttribute("aria-current", true);
        
        // Auto-scroll thumbnail into view (vertical gallery)
        const thumbnailList = this.querySelector('.thumbnail-slider__list, .thumbnail-list');
        if (thumbnailList) {
          const thumbTop = thumbnail.offsetTop;
          const thumbHeight = thumbnail.offsetHeight;
          const listScrollTop = thumbnailList.scrollTop;
          const listHeight = thumbnailList.clientHeight;
          
          // Check if thumbnail is out of view
          if (thumbTop < listScrollTop) {
            // Thumbnail is above visible area
            thumbnailList.scrollTo({ top: thumbTop, behavior: 'smooth' });
          } else if (thumbTop + thumbHeight > listScrollTop + listHeight) {
            // Thumbnail is below visible area
            thumbnailList.scrollTo({ top: thumbTop - listHeight + thumbHeight, behavior: 'smooth' });
          }
          return;
        }
        
        // Fallback for horizontal slider
        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
        if (this.elements.thumbnails.slider) {
          this.elements.thumbnails.slider.scrollTo({
            left: thumbnail.offsetLeft,
          });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector(
          ".product__modal-opener--image img"
        );
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute("aria-hidden", false);
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace(
              "[index]",
              position
            );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute("aria-hidden", true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector(".deferred-media");
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader =
          this.stickyHeader || document.querySelector("sticky-header");
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event("preventHeaderReveal"));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute("role", "presentation");
        this.elements.viewer.sliderItems.forEach((slide) =>
          slide.setAttribute("role", "presentation")
        );
      }
    }
  );
}
